import { addToCart } from '@theme/pj-cart';

/**
 * Quick add on Politics Jeans product cards.
 *
 * Tapping a size adds it through `addToCart` (assets/pj-cart.js), which posts to
 * /cart/add.js and dispatches the standard cart events.
 *
 * Markup contract (snippets/pj-product-card.liquid):
 *   <pj-quick-add>
 *     <button data-pj-quick-toggle aria-expanded="false">   (mobile only)
 *     <div data-pj-quick-panel>
 *       <button data-pj-add="VARIANT_ID">32</button>
 *       <a class="pj-size--soldout" href="...">34</a>        (Notify me)
 *     <p data-pj-quick-status role="status">
 */
class PjQuickAdd extends HTMLElement {
  #abort = new AbortController();
  #busy = false;

  connectedCallback() {
    const { signal } = this.#abort;
    this.addEventListener('click', this.#onClick, { signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const toggle = target.closest('[data-pj-quick-toggle]');
    if (toggle) {
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(open));
      this.toggleAttribute('data-open', open);
      return;
    }

    const button = /** @type {HTMLButtonElement | null} */ (target.closest('[data-pj-add]'));
    if (button) {
      event.preventDefault();
      this.#add(button);
    }
  };

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    if (this.#busy) return;
    const variantId = button.dataset.pjAdd;
    if (!variantId) return;

    this.#busy = true;
    button.setAttribute('aria-busy', 'true');
    this.#status('');

    const result = await addToCart(this, variantId, 1, { productId: this.dataset.productId });
    this.#busy = false;
    button.removeAttribute('aria-busy');

    if (!result.ok) {
      this.#status(result.message ?? 'Could not add to bag');
      return;
    }

    button.setAttribute('data-added', '');
    setTimeout(() => button.removeAttribute('data-added'), 1400);
    this.#status(`${button.dataset.sizeLabel ?? ''} added to bag`.trim());
    this.removeAttribute('data-open');
    this.querySelector('[data-pj-quick-toggle]')?.setAttribute('aria-expanded', 'false');
  }

  /** @param {string} message */
  #status(message) {
    const el = this.querySelector('[data-pj-quick-status]');
    if (el) el.textContent = message;
  }
}

if (!customElements.get('pj-quick-add')) customElements.define('pj-quick-add', PjQuickAdd);
